import { writable } from 'svelte/store';

/**
 * Tracks tasks that were just added, client-side only.
 *
 * Used to briefly highlight a newly created task in task lists (ProjectRail,
 * Projects/Show, ProjectKanbanBoard) so the user can see where in the list it
 * landed. Consumers add a `just-added-task` class when the task id is present
 * in the store; the store removes ids after RECENTLY_ADDED_TTL_MS so the
 * highlight fades and cleans itself up.
 *
 * Both the optimistic temp id and the real created id are marked by callers,
 * so the highlight persists across the temp → real task swap in the cache.
 */

/** How long a newly added task stays highlighted. */
export const RECENTLY_ADDED_TTL_MS = 5000;

/** Set of task ids that were recently added. Subscribe for reactivity. */
export const recentlyAddedTasks = writable<ReadonlySet<string>>(new Set());

const expiryTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Mark a task as recently added so its list item gets the highlight animation. */
export function markTaskRecentlyAdded(taskId: string) {
  recentlyAddedTasks.update((ids) => new Set(ids).add(taskId));
  const existing = expiryTimers.get(taskId);
  if (existing) clearTimeout(existing);
  expiryTimers.set(
    taskId,
    setTimeout(() => {
      expiryTimers.delete(taskId);
      recentlyAddedTasks.update((ids) => {
        const next = new Set(ids);
        next.delete(taskId);
        return next;
      });
    }, RECENTLY_ADDED_TTL_MS),
  );
}
